import { useEffect, useState } from "react";
import "@/App.css";
import { Toaster } from "sonner";
import { motion } from "framer-motion";
import { ArrowUpRight, Film, LayoutGrid, FileText, MonitorSmartphone, ShieldCheck, Menu, X } from "lucide-react";
import { BRAND, TOTAL_DURATION } from "@/data/adPackage";
import AdPlayer from "@/components/AdPlayer";
import Storyboard from "@/components/Storyboard";
import ScriptDownloads from "@/components/ScriptDownloads";
import Mockups from "@/components/Mockups";
import BrandNotes from "@/components/BrandNotes";

const NAV = [
  { id: "player", label: "Player", icon: Film },
  { id: "storyboard", label: "Storyboard", icon: LayoutGrid },
  { id: "script", label: "Script & Downloads", icon: FileText },
  { id: "mockups", label: "App UI", icon: MonitorSmartphone },
  { id: "notes", label: "Brand & Legal", icon: ShieldCheck },
];

function Section({ id, kicker, title, subtitle, children }) {
  return (
    <section id={id} className="scroll-mt-24 py-16 sm:py-24">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-8 sm:mb-12 max-w-3xl"
        >
          <div className="flex items-center gap-2 mb-3">
            <span className="w-8 h-px bg-orange-400" />
            <span className="text-xs font-semibold tracking-[0.25em] uppercase text-orange-400">{kicker}</span>
          </div>
          <h2 className="font-display font-black text-white text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-none">{title}</h2>
          {subtitle && <p className="mt-4 text-neutral-400 text-base sm:text-lg leading-relaxed">{subtitle}</p>}
        </motion.div>
        {children}
      </div>
    </section>
  );
}

function App() {
  const [open, setOpen] = useState(false);
  const [teProvider, setTeProvider] = useState(null);
  const scrollTo = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    document.title = "BiteGoFood — Launch Commercial Creative Package";
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/voiceover/meta`)
      .then((r) => r.json())
      .then((d) => setTeProvider(d?.te?.provider))
      .catch(() => {});
  }, []);

  return (
    <div className="App min-h-screen bg-[#0A0A0A]">
      <Toaster position="top-center" theme="dark" richColors />

      {/* Nav */}
      <header className="fixed top-0 inset-x-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <button onClick={() => scrollTo("top")} className="flex items-center gap-2.5" data-testid="nav-logo">
            <img src={BRAND.logo} alt="BiteGoFood" className="w-9 h-9 rounded-lg" />
            <span className="font-display font-black text-white text-lg tracking-tight">BiteGoFood</span>
            <span className="hidden sm:inline text-[10px] font-semibold tracking-widest uppercase text-neutral-500 border-l border-white/10 pl-2.5">Launch Package</span>
          </button>
          <nav className="hidden md:flex items-center gap-1">
            {NAV.map((n) => (
              <button
                key={n.id}
                data-testid={`nav-${n.id}`}
                onClick={() => scrollTo(n.id)}
                className="text-sm text-neutral-300 hover:text-white px-3 py-2 rounded-lg hover:bg-white/5 transition-colors duration-200"
              >
                {n.label}
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <a
              href={BRAND.websiteUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="nav-cta"
              className="hidden sm:inline-flex items-center gap-1.5 brand-gradient text-white font-semibold text-sm px-4 py-2 rounded-full hover:scale-105 transition-transform duration-200"
            >
              bitego.org <ArrowUpRight className="w-4 h-4" />
            </a>
            <button className="md:hidden text-white p-2" onClick={() => setOpen((o) => !o)} data-testid="nav-toggle" aria-label="Menu">
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {open && (
          <div className="md:hidden border-t border-white/10 px-5 py-3 space-y-1">
            {NAV.map((n) => (
              <button key={n.id} onClick={() => scrollTo(n.id)} className="flex items-center gap-3 w-full text-left text-neutral-200 py-2.5">
                <n.icon className="w-4 h-4 text-orange-400" /> {n.label}
              </button>
            ))}
            <a href={BRAND.websiteUrl} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 brand-gradient text-white font-semibold text-sm px-4 py-2 rounded-full w-max mt-2">
              bitego.org <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        )}
      </header>

      {/* Hero + player */}
      <div id="top" className="pt-24 sm:pt-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-4xl">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className="text-xs font-semibold tracking-[0.2em] uppercase text-orange-400 glass rounded-full px-3 py-1">Product Launch Commercial</span>
              <span className="text-xs font-semibold tracking-[0.2em] uppercase text-neutral-400 glass rounded-full px-3 py-1">16:9 · {TOTAL_DURATION}s</span>
              <span className="text-xs font-semibold tracking-[0.2em] uppercase text-neutral-400 glass rounded-full px-3 py-1">Mute-friendly</span>
            </div>
            <h1 className="font-display font-black text-white text-4xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.92]">
              The BiteGoFood <span className="brand-text">launch film</span>, ready to play.
            </h1>
            <p className="mt-5 text-neutral-400 text-base sm:text-xl max-w-2xl leading-relaxed">
              A premium, factually-grounded commercial that takes restaurant owners from pain to <span className="text-white font-medium">bitego.org</span> — leading with the approved <span className="text-white font-medium">18% commission</span> claim and transparent pricing. Press play, or switch to the text-led no-VO cut.
            </p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }} className="mt-8 sm:mt-10">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs font-bold tracking-[0.2em] uppercase brand-gradient text-white rounded-full px-3 py-1">Version 1 · English</span>
              <span className="text-xs text-neutral-500">Professional Indian-English voice-over</span>
            </div>
            <AdPlayer lang="en" />
            <p className="mt-3 text-xs text-neutral-500 text-center">
              Cinematic sequence rendered in your browser — screen-record or export for YouTube, Instagram, WhatsApp & outreach. Voice-over is AI-generated neutral Indian-English.
            </p>
          </motion.div>

          {/* Telugu version */}
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="mt-14 sm:mt-16">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="text-xs font-bold tracking-[0.2em] uppercase brand-gradient text-white rounded-full px-3 py-1">Version 2 · తెలుగు</span>
              <span className="text-xs text-neutral-500">
                Telugu voice-over · English on-screen graphics · Telugu subtitles
              </span>
            </div>
            <AdPlayer lang="te" />
            <p className="mt-3 text-xs text-neutral-500 text-center" data-testid="te-provider-note">
              {teProvider === "elevenlabs"
                ? "Telugu narration by a professional native Telugu male voice (ElevenLabs). Background music is off by default for maximum clarity."
                : "Telugu narration is an OpenAI draft (mild non-native accent). Add an ElevenLabs API key to upgrade to a natural native Telugu voice."}
            </p>
          </motion.div>
        </div>
      </div>

      <div className="border-t border-white/5 mt-16">
        <Section id="player" kicker="How to use it" title="Two versions, one player" subtitle="Toggle between the professional voice-over cut and a stronger text-led no-VO cut. Use the 9:16 guide to check that every key element stays center-safe for vertical crops. Chapters let you jump scene to scene.">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { t: "Main VO version", d: "Confident Indian-English narration, subtitles and subtle music bed." },
              { t: "No-VO text-led version", d: "Same visuals, stronger on-screen messaging — built to sell on mute." },
              { t: "Burned-in subtitles", d: "High-contrast, safe-margin captions matching the VO near-exactly." },
              { t: "9:16 safe framing", d: "Key text, logo, UI and faces held center for future vertical cuts." },
            ].map((c) => (
              <div key={c.t} className="rounded-2xl border border-white/10 bg-neutral-950 p-5">
                <h3 className="font-display font-bold text-white text-lg">{c.t}</h3>
                <p className="mt-1.5 text-sm text-neutral-400 leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
        </Section>
      </div>

      <Section id="storyboard" kicker="Scene by scene" title="7-scene storyboard" subtitle="The full narrative arc — problem, platform, pricing, product, customer, growth and call-to-action — with the exact visual, voice-over and on-screen text for each beat.">
        <Storyboard />
      </Section>

      <Section id="script" kicker="Creative lock" title="Script sheet & downloads" subtitle="The approved voice-over, generated audio and every deliverable — script, subtitles, shot list and the 9:16 cut guide.">
        <ScriptDownloads />
      </Section>

      <Section id="mockups" kicker="Shown, not exaggerated" title="BiteGoFood app UI mockups" subtitle="Clean, accurate product visuals used in the film — real restaurant tools and a transparent checkout, kept generic and free of any competitor-coded design.">
        <Mockups />
      </Section>

      <Section id="notes" kicker="Guardrails" title="Brand & legal safety" subtitle="Every claim in this film is deliberately constrained. Here is exactly what is and isn't said.">
        <BrandNotes />
      </Section>

      {/* Footer */}
      <footer id="about" className="border-t border-white/10 bg-neutral-950">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8">
          {/* Company + founder */}
          <div className="lg:col-span-6" data-testid="footer-about">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-8 h-px bg-orange-400" />
              <span className="text-[11px] font-semibold tracking-[0.25em] uppercase text-orange-400">About Vijay Software Solutions</span>
            </div>
            <p className="text-sm text-neutral-300 leading-relaxed max-w-xl">
              Vijay Software Solutions Pvt Ltd is a technology company focused on building modern software products, digital platforms, AI-powered solutions, and technology services for businesses and users.
            </p>
            <p className="mt-3 text-sm text-neutral-400 leading-relaxed max-w-xl">
              BiteGoFood is a product of Vijay Software Solutions Pvt Ltd, built to connect customers, restaurants, and delivery partners through a modern food delivery platform.
            </p>
            <div className="mt-5 h-px w-full max-w-xl bg-white/10" />
            <div className="mt-4" data-testid="footer-founder">
              <div className="text-sm font-semibold text-white">Founded by Perikala Bhargav</div>
              <div className="text-xs text-neutral-500">Founder &amp; CEO, Vijay Software Solutions Pvt Ltd</div>
            </div>
          </div>

          {/* BiteGoFood brand block */}
          <div className="lg:col-span-3">
            <div className="flex items-center gap-3">
              <img src={BRAND.logo} alt="BiteGoFood" className="w-12 h-12 rounded-2xl shadow-lg" />
              <div>
                <div className="font-display font-black text-white text-lg leading-none">BiteGoFood</div>
                <div className="text-xs text-neutral-500 mt-1">{BRAND.tagline}</div>
              </div>
            </div>
            <a
              href="https://bitego.org/"
              target="_blank"
              rel="noreferrer"
              data-testid="footer-cta"
              className="mt-5 inline-flex items-center gap-1.5 brand-gradient text-white font-semibold text-sm px-4 py-2.5 rounded-full hover:scale-105 transition-transform duration-200"
            >
              Visit bitego.org <ArrowUpRight className="w-4 h-4" />
            </a>
            <p className="mt-3 text-xs text-neutral-600">https://bitego.org/</p>
          </div>

          {/* Useful links */}
          <div className="lg:col-span-3" data-testid="footer-links">
            <div className="text-[11px] font-semibold tracking-[0.25em] uppercase text-orange-400 mb-4">Useful Links</div>
            <ul className="space-y-2.5 text-sm">
              <li>
                <button onClick={() => scrollTo("about")} className="text-neutral-300 hover:text-white transition-colors duration-200" data-testid="link-about">About the Company</button>
              </li>
              <li>
                <a href={`mailto:${BRAND.partnerEmail}`} className="text-neutral-300 hover:text-white transition-colors duration-200" data-testid="link-contact">Contact</a>
              </li>
              <li>
                <a href="https://bitego.org/" target="_blank" rel="noreferrer" className="text-neutral-300 hover:text-white transition-colors duration-200" data-testid="link-privacy">Privacy Policy</a>
              </li>
              <li>
                <a href="https://bitego.org/" target="_blank" rel="noreferrer" className="text-neutral-300 hover:text-white transition-colors duration-200" data-testid="link-terms">Terms &amp; Conditions</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <p className="text-xs text-neutral-600">© {new Date().getFullYear()} Vijay Software Solutions Pvt Ltd · BiteGoFood. All rights reserved.</p>
            <p className="text-xs text-neutral-600">{BRAND.poweredBy}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
